import React from 'react';
import { alpha, Components, TextField, Theme } from '@mui/material';
import {
    CancelRounded,
    CheckCircleOutline,
    ErrorOutline,
    InfoOutlined,
    WarningAmberOutlined,
} from '@mui/icons-material';
import get from 'lodash/get';

import MierBBookOtf from './fonts/MierB-Book.otf';
import MierBBookItalicOtf from './fonts/MierB-BookItalic.otf';
import MierBDemiOtf from './fonts/MierB-Demi.otf';
import MierBDemiItalicOtf from './fonts/MierB-DemiItalic.otf';
import MierBRegularOtf from './fonts/MierB-Regular.otf';
import MierBRItalicOtf from './fonts/MierB-Italic.otf';
import MierBBoldItalicOtf from './fonts/MierB-BoldItalic.otf';
import MierBBoldOtf from './fonts/MierB-Bold.otf';
import slotStyle from './slotStyle';
import typography from './typography';
import { outlineBlue } from './colors';
import type { AppPalette } from './palette';
import shadows, { DS_ELEVATION_CARD } from './shadows';
import { CHECKBOX_RADIUS } from './shape';
import { touchTarget } from './mixins';

import WarningCircle from 'assets/icons/input-warning-circle.svg';
import CheckboxChecked from 'modules/common/components/Icons/CheckboxChecked';
import CheckboxIndeterminate from 'modules/common/components/Icons/CheckboxIndeterminate';
import CheckboxUnchecked from 'modules/common/components/Icons/CheckboxUnchecked';
import RadioChecked from 'modules/common/components/Icons/RadioChecked';
import RadioUnchecked from 'modules/common/components/Icons/RadioUnchecked';
import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';
import ChevronLeftIcon from 'modules/common/components/Icons/ChevronLeftIcon';

/** Component overrides read from the palette passed in, so they follow its mode. */
export default function createComponents(palette: AppPalette): Components {
    const { grey, cement, ink } = palette;
    const isDark = palette.mode === 'dark';
    // Dark floating surfaces sit one step above the page, since shadows don't read on dark.
    const darkFloating = { backgroundColor: grey[100], border: `1px solid ${grey[200]}` };

    return {
        MuiCssBaseline: {
            styleOverrides: `
            body {
                overscroll-behavior: none;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBDemiItalicOtf}) format('opentype');
                font-weight: 600;
                font-style: italic;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBDemiOtf}) format('opentype');
                font-weight: 600;
                font-style: normal;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBBookItalicOtf}) format('opentype');
                font-weight: 500;
                font-style: italic;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBBookOtf}) format('opentype');
                font-weight: 500;
                font-style: normal;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBRegularOtf}) format('opentype');
                font-weight: 400;
                font-style: normal;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBRItalicOtf}) format('opentype');
                font-weight: 400;
                font-style: italic;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBBoldItalicOtf}) format('opentype');
                font-weight: 700;
                font-style: italic;
                font-display: swap;
            }

            @font-face {
                font-family: 'Mier B';
                src: url(${MierBBoldOtf}) format('opentype');
                font-weight: 700;
                font-style: normal;
                font-display: swap;
            }
      `,
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
                color: 'secondary',
            },
            styleOverrides: {
                root: slotStyle({
                    variants: [
                        {
                            props: { borderless: true },
                            style: {
                                '& .MuiInputBase-root:not(.Mui-focused)': {
                                    borderColor: 'transparent',
                                },
                            },
                        },
                        {
                            props: ({ backgroundVariant }: { backgroundVariant?: string }) => !!backgroundVariant,
                            style: ({ backgroundVariant }: { backgroundVariant: string }) => {
                                const colorPalette = get(palette, backgroundVariant, 'white');
                                return {
                                    '& .MuiInputBase-root': {
                                        backgroundColor: colorPalette,
                                    },
                                };
                            },
                        },
                        {
                            props: { readOnlyOutput: true },
                            style: {
                                // Reads as output rather than an input that will not take focus, so every
                                // edge the variants above draw has to come off. Which edge that is depends
                                // on the variant: the underline for `standard`, which is what TextField
                                // defaults to here, and the root border for `outlined`. The fieldset this
                                // used to target is set `display: none` globally, so it never drew one.
                                '& .MuiInputBase-root': {
                                    backgroundColor: grey[100],
                                    cursor: 'default',
                                    border: 'none',
                                },
                                '& .MuiInput-underline': { '&:before, &:after': { borderBottom: 'none' } },
                                '& .MuiInputBase-input': { color: grey[500], cursor: 'default' },
                            },
                        },
                    ],
                }),
            },
        },
        MuiButtonGroup: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variant: 'body1',
                variantMapping: {
                    body1paragraph: 'p',
                    body2paragraph: 'p',
                },
            },
            variants: [
                {
                    props: { variant: 'sectionHeader' },
                    style: {
                        ...typography.h6,
                        marginBottom: '16px',
                    },
                },
            ],
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.background.paper,
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderBottomColor: palette.cement[100],
                    borderRadius: '8px',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    border: 0,
                    boxShadow: `0px 8px 64px 0px ${alpha(palette.common.black, 0.12)}`,
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                // The ripple is off across the app, so focus is the only feedback a keyboard user
                // gets — and `MuiButton` was the only component that defined any. Anything built on
                // a bare `ButtonBase` had none at all. Same outline as `MuiButton` below, so a
                // focused control looks the same wherever it came from.
                root: {
                    '&.Mui-focusVisible': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 12,
                    '&.Mui-disabled': {
                        color: palette.grey[300],
                    },
                    '&.Mui-focusVisible': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                    },
                },
                text: slotStyle({
                    variants: [
                        {
                            props: (props: { children?: React.ReactChild | string }) =>
                                typeof props.children === 'string',
                            style: (props: { loadingPosition?: string }) => {
                                if (props.loadingPosition !== 'center') {
                                    return undefined;
                                }
                                return {
                                    '&.MuiButton-loading': {
                                        color: 'transparent',
                                    },
                                };
                            },
                        },
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette = palette[props.color as keyof typeof palette] as unknown as {
                                    lightHover: string;
                                    light: string;
                                };
                                if (!colorPalette || !colorPalette.lightHover || !colorPalette.light) {
                                    return undefined;
                                }

                                return {
                                    ':hover': {
                                        backgroundColor: colorPalette.lightHover,
                                    },
                                    ':active': {
                                        backgroundColor: 'transparent',
                                    },
                                };
                            },
                        },
                    ],
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                    },
                }),
                contained: slotStyle({
                    '&.Mui-disabled': {
                        backgroundColor: palette.grey[100],
                    },
                    variants: [
                        {
                            props: (props: { children?: React.ReactChild | string }) =>
                                typeof props.children === 'string',
                            style: (props: { loadingPosition?: string }) => {
                                if (props.loadingPosition !== 'center') {
                                    return undefined;
                                }
                                return {
                                    '&.MuiButton-loading': {
                                        color: 'transparent',
                                    },
                                };
                            },
                        },
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette = palette[props.color as keyof typeof palette] as unknown as {
                                    main: string;
                                    hover: string;
                                    pressed?: string;
                                    disabled?: string;
                                    disabledText?: string;
                                };
                                if (!colorPalette || !colorPalette.hover) {
                                    return undefined;
                                }

                                return {
                                    ':hover': {
                                        backgroundColor: colorPalette.hover,
                                    },
                                    ':active': {
                                        backgroundColor: colorPalette.pressed ?? colorPalette.main,
                                    },
                                    ...(colorPalette.disabled && {
                                        '&.Mui-disabled': {
                                            backgroundColor: colorPalette.disabled,
                                            color: colorPalette.disabledText,
                                        },
                                    }),
                                };
                            },
                        },
                    ],
                }),
                outlined: slotStyle({
                    '&.MuiButton-sizeLarge': {
                        padding: '13px 27px',
                    },
                    '&.MuiButton-sizeMedium': {
                        padding: '11px 23px',
                    },
                    '&.MuiButton-sizeSmall': {
                        padding: '9px 19px',
                    },
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                        borderColor: palette.grey[100],
                    },
                    variants: [
                        {
                            props: (props: { children?: React.ReactChild | string }) =>
                                typeof props.children === 'string',
                            style: (props: { loadingPosition?: string }) => {
                                if (props.loadingPosition !== 'center') {
                                    return undefined;
                                }
                                return {
                                    '&.MuiButton-loading': {
                                        color: 'transparent',
                                    },
                                };
                            },
                        },
                        {
                            props: { color: 'neutral' },
                            style: {
                                borderColor: palette.grey[200],
                                color: palette.text.primary,
                                ':hover': {
                                    backgroundColor: palette.grey[50],
                                    borderColor: palette.grey[300],
                                },
                                ':active': {
                                    backgroundColor: 'transparent',
                                    borderColor: palette.grey[200],
                                },
                            },
                        },
                        {
                            props: (props: { color: string }) => !!props.color && props.color !== 'neutral',
                            style: (props: { color: string }) => {
                                const colorPalette = palette[props.color as keyof typeof palette] as unknown as {
                                    main: string;
                                    lightHover: string;
                                    light: string;
                                };

                                if (!colorPalette || !colorPalette.lightHover || !colorPalette.main) {
                                    return undefined;
                                }

                                return {
                                    borderColor: colorPalette.main,
                                    color: colorPalette.main,
                                    ':hover': {
                                        backgroundColor: colorPalette.lightHover,
                                        borderColor: colorPalette.lightHover,
                                    },
                                    ':active': {
                                        backgroundColor: 'transparent',
                                        borderColor: colorPalette.main,
                                    },
                                };
                            },
                        },
                    ],
                }),
                sizeLarge: {
                    fontFamily: "'Mier B', sans-serif",
                    fontSize: '1rem', // 16px
                    letterSpacing: 0,
                    lineHeight: '1.25rem', // 20px
                    fontWeight: 600,
                    padding: '14px 28px',
                },
                sizeMedium: {
                    ...typography.button,
                    padding: '12px 24px',
                    lineHeight: '1.25rem',
                },
                sizeSmall: {
                    ...typography.button,
                    padding: '10px 20px',
                    lineHeight: '1.25rem',
                },
            },
            variants: [
                {
                    // Day-first scheduler time slot: white fill, purple/400 outline, purple/100 hover
                    // (matches the outlined-primary instant-callback button). Callers set minHeight via sx.
                    props: { variant: 'timeSlot' },
                    style: {
                        ...typography.h4,
                        color: palette.text.primary,
                        backgroundColor: palette.background.paper,
                        border: `1px solid ${palette.purple[400]}`,
                        '&:hover': {
                            backgroundColor: palette.purple[100],
                            borderColor: palette.purple[100],
                        },
                    },
                },
                {
                    // Selected day-first time slot: white fill, emphasized 2px text.primary border + lift
                    // (the day-first picker's active state). Callers set minHeight via sx.
                    props: { variant: 'timeSlotSelected' },
                    style: {
                        ...typography.h4,
                        color: palette.text.primary,
                        backgroundColor: palette.background.paper,
                        border: `2px solid ${palette.text.primary}`,
                        boxShadow: shadows[2],
                        '&:hover': {
                            borderWidth: 2,
                            borderColor: palette.text.primary,
                        },
                    },
                },
            ],
        },
        MuiAvatar: {
            styleOverrides: {
                fallback: {
                    ...typography.body1,
                },
                root: {
                    fontWeight: 500,
                    variants: [
                        {
                            props: ({ variant }) => variant === 'small',
                            style: {
                                width: 24,
                                height: 24,
                                ...typography.caption,
                            },
                        },
                        {
                            props: ({ variant }) => variant === 'medium',
                            style: {
                                width: 32,
                                height: 32,
                                ...typography.subtitle2,
                            },
                        },
                        {
                            props: ({ variant }) => variant === 'large',
                            style: {
                                width: 40,
                                height: 40,
                                ...typography.subtitle1,
                            },
                        },
                        {
                            props: ({ color }) => color === 'primary',
                            style: {
                                backgroundColor: palette.cement[100],
                                color: palette.cement[700],
                            },
                        },
                        {
                            props: ({ color }) => color === 'secondary',
                            style: {
                                backgroundColor: palette.purple[500],
                                color: palette.onColor,
                            },
                        },
                        {
                            props: ({ color }) => color === 'success',
                            style: {
                                backgroundColor: palette.success.main,
                                color: palette.success.contrastText,
                            },
                        },
                        {
                            props: ({ variant }) => variant === 'bankIcon',
                            style: {
                                width: 40,
                                height: 40,
                                backgroundColor: palette.primary.main,
                                color: palette.primary.contrastText,
                                fontSize: typography.subtitle2.fontSize,
                                fontWeight: typography.subtitle2.fontWeight,
                            },
                        },
                    ],
                },
            },
            defaultProps: {
                variant: 'large',
                color: 'primary',
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...typography.subtitle1,
                    color: palette.text.primary,
                    lineHeight: 1.3,
                    '& + .MuiInput-root': {
                        marginTop: '8px',
                    },
                },
                standard: {
                    position: 'initial',
                    fontSize: '1em',
                    transform: 'none',
                },
                outlined: {
                    position: 'absolute',
                    fontSize: '1em',
                    transform: 'none',
                    left: '17px',
                    top: '18px',
                    transition: 'all 0.2s ease-in-out',
                    '&.MuiInputLabel-shrink': {
                        top: '8px',
                        fontSize: '0.75em',
                        input: {
                            marginTop: '8px',
                        },
                    },
                    '&.MuiInputLabel-root': {
                        color: palette.grey[500],
                    },
                    '&.MuiInputLabel-sizeSmall': {
                        top: '13px',
                        '&.MuiInputLabel-shrink': {
                            top: '4px',
                            fontSize: '0.75em',
                            input: {
                                marginTop: '8px',
                            },
                        },
                    },
                },
            },
            defaultProps: {
                color: 'secondary',
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    ...typography.subtitle1,
                    color: palette.text.primary,
                    '&.Mui-disabled': {
                        color: palette.grey[300],
                    },
                    '&.Mui-error': {
                        color: palette.error.main,
                    },
                    '&.Mui-focused': {
                        color: palette.secondary.main,
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '.MuiIconButton-root': {
                        padding: 0,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    ...typography.caption,
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '6px',
                    gap: '4px',
                    marginLeft: 0,
                    '&.Mui-error': {
                        '&::before': {
                            content: '""',
                            width: 16,
                            height: 16,
                            background: `url("${WarningCircle}")`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            display: 'inline-block',
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: slotStyle({
                    ...typography.body1,
                    '.MuiInputAdornment-root': {
                        minWidth: 16,
                        minHeight: 16,
                    },
                    variants: [
                        {
                            // A value read off a document, shown the way the document shows it:
                            // monospace so digits line up column-wise between one field and the next,
                            // and right-aligned because that is the edge an amount is read from. The
                            // input carries no padding of its own -- whatever draws the box owns the
                            // spacing, so a value sits on the same line as the marker beside it.
                            props: { extractedValue: true },
                            style: {
                                ...typography.body2,
                                fontFamily: 'monospace',
                                '& .MuiInputBase-input': {
                                    textAlign: 'right',
                                    padding: 0,
                                    textOverflow: 'ellipsis',
                                },
                                // A box with no field behind it is disabled, and a disabled input
                                // greys its text. Here the box is empty, so the grey would land on
                                // the placeholder that says why -- which is the one thing to read.
                                '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'unset' },
                            },
                        },
                        {
                            // Keeps the row the height it was, so the grid does not jump when a cell opens.
                            props: ({ gridCell }: { gridCell?: string }) => !!gridCell,
                            style: {
                                ...typography.body2,
                                borderBottom: `2px solid ${palette.secondary.main}`,
                                '&.Mui-error': { borderBottomColor: palette.error.main },
                                '& .MuiInputBase-input': { padding: 0, height: 'auto', textOverflow: 'ellipsis' },
                            },
                        },
                        {
                            props: { gridCell: 'amount' },
                            style: {
                                '& .MuiInputBase-input': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
                            },
                        },
                    ],
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: slotStyle({
                    borderRadius: 8,
                    lineHeight: 1.5,
                    border: '1px solid',
                    transition: 'border-color, background-color 0.2s ease-in-out',
                    padding: '15px',
                    background: palette.background.paper,
                    '&.Mui-disabled': {
                        borderColor: palette.grey[300],
                        ':hover': {
                            borderColor: palette.grey[300],
                            '.MuiSvgIcon-root': {
                                color: palette.grey[300],
                            },
                        },
                        '.MuiSvgIcon-root': {
                            color: palette.grey[300],
                        },
                    },
                    '.MuiSvgIcon-root': {
                        fontSize: '1rem',
                    },
                    '&.Mui-error': {
                        borderColor: palette.error.light,
                        backgroundColor: palette.red[50],
                        ':hover': {
                            borderColor: palette.error.main,
                        },
                        ':active': {
                            borderColor: palette.error.main,
                            backgroundColor: palette.background.paper,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.error.main,
                            borderWidth: 2,
                            backgroundColor: palette.background.paper,
                            padding: '14px',
                        },
                    },
                    fieldset: {
                        display: 'none',
                    },
                    '.MuiInputLabel-shrink ~ &': {
                        paddingTop: '23px',
                        paddingBottom: '7px',
                        '&.MuiSelect-root': {
                            paddingTop: '12px',
                            paddingBottom: '0px',
                        },
                        '&.Mui-focused': {
                            paddingTop: '22px',
                            paddingBottom: '6px',
                        },
                    },
                    '.MuiInputLabel-shrink ~ &.MuiInputBase-sizeSmall': {
                        paddingTop: '17px',
                        paddingBottom: '3px',
                        '&.Mui-focused': {
                            paddingTop: '16px',
                            paddingBottom: '2px',
                        },
                    },
                    input: {
                        padding: 0,
                        height: 'initial',
                        '&.MuiInputBase-inputAdornedStart': {
                            marginLeft: '16px',
                        },
                        '&.MuiInputBase-inputAdornedEnd': {
                            marginRight: '16px',
                        },
                    },
                    variants: [
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette = palette[props.color as keyof typeof palette] as unknown as {
                                    main: string;
                                    light: string;
                                };
                                if (!colorPalette || !colorPalette.main || !colorPalette.light) {
                                    return undefined;
                                }

                                return {
                                    borderColor: colorPalette.light,
                                    ':hover': {
                                        borderColor: colorPalette.main,
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    ':active': {
                                        borderColor: colorPalette.main,
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    '&.Mui-focused': {
                                        borderColor: colorPalette.main,
                                        borderWidth: 2,
                                        padding: '14px',
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: colorPalette.light,
                                    },
                                };
                            },
                        },
                    ],
                }),
                sizeSmall: slotStyle({
                    padding: '10px 15px',
                    variants: [
                        {
                            style: () => ({
                                '&.Mui-focused': {
                                    padding: '9px 14px',
                                },
                            }),
                        },
                    ],
                }),
            },
        },
        MuiInput: {
            styleOverrides: {
                root: slotStyle({
                    background: palette.background.paper,
                    border: '1px solid',
                    lineHeight: 1.5,
                    transition: 'border-color, background-color 0.2s ease-in-out',
                    '&:after': {
                        display: 'none',
                    },
                    '&:before': {
                        display: 'none',
                    },
                    '&.Mui-disabled': {
                        borderColor: palette.grey[300],
                        ':hover': {
                            borderColor: palette.grey[300],
                            '.MuiSvgIcon-root': {
                                color: palette.grey[300],
                            },
                        },
                        '.MuiSvgIcon-root': {
                            color: palette.grey[300],
                        },
                    },
                    borderRadius: 8,
                    padding: '11px 16px',
                    '.MuiSvgIcon-root': {
                        fontSize: '1rem',
                    },
                    '&.Mui-error': {
                        borderColor: palette.error.light,
                        backgroundColor: palette.red[50],
                        ':hover': {
                            borderColor: palette.error.main,
                        },
                        ':active': {
                            borderColor: palette.error.main,
                            backgroundColor: palette.background.paper,
                        },
                        '&.Mui-focused': {
                            borderColor: palette.error.main,
                            borderWidth: 2,
                            backgroundColor: palette.background.paper,
                            padding: '10px 15px',
                        },
                    },
                    variants: [
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette = palette[props.color as keyof typeof palette] as unknown as {
                                    main: string;
                                    light: string;
                                };
                                if (!colorPalette || !colorPalette.main || !colorPalette.light) {
                                    return undefined;
                                }

                                return {
                                    borderColor: colorPalette.light,
                                    ':hover': {
                                        borderColor: colorPalette.main,
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    ':active': {
                                        borderColor: colorPalette.main,
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    '&.Mui-focused': {
                                        borderColor: colorPalette.main,
                                        borderWidth: 2,
                                        padding: '10px 15px',
                                        '.MuiSvgIcon-root': {
                                            color: colorPalette.main,
                                        },
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: colorPalette.light,
                                    },
                                };
                            },
                        },
                    ],
                }),
                input: {
                    padding: 0,
                    height: 'initial',
                    '&.MuiInputBase-inputAdornedStart': {
                        marginLeft: '16px',
                    },
                    '&.MuiInputBase-inputAdornedEnd': {
                        marginRight: '16px',
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                outlined: {
                    margin: 0,
                },
                standard: {
                    margin: 0,
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: false,
            },
            styleOverrides: {
                tooltip: {
                    ...typography.subtitle2,
                    backgroundColor: palette.background.default,
                    color: palette.secondary.main,
                    borderRadius: 8,
                    padding: '12px 16px',
                    boxShadow: `0px 1px 15px 0px ${alpha(palette.secondary.main, 0.15)}`,
                },
                arrow: {
                    display: 'none',
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    '&.MuiFilledInput-underline': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                    },
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                indicatorColor: 'secondary',
                textColor: 'secondary',
            },
            styleOverrides: {
                root: {
                    ...typography.body2,
                    textTransform: 'none',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    ...typography.subtitle1,
                    textTransform: 'none',
                    paddingLeft: '32px',
                    paddingRight: '32px',
                    paddingTop: '15px',
                    paddingBottom: '15px',
                    '& .MuiTabs-scrollButtons': {
                        '&.Mui-disabled': {
                            opacity: 0.5,
                        },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '100px',
                },
                sizeMedium: {
                    padding: '8px 14px',
                },
                sizeSmall: {
                    padding: '4px 10px',
                },
                label: {
                    ...typography.caption,
                    fontWeight: 500,
                    padding: 0,
                    '.MuiChip-icon + &': {
                        paddingLeft: 11,
                    },
                    variants: [
                        {
                            props: ({ onDelete }) => !!onDelete,
                            style: {
                                paddingRight: 6,
                            },
                        },
                    ],
                },
                deleteIcon: {
                    marginRight: -6,
                    marginLeft: 0,
                    fontSize: '1rem',
                },
                filled: slotStyle({
                    '&.Mui-disabled': {
                        backgroundColor: palette.grey[100],
                    },
                    variants: [
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette =
                                    props.color === 'default'
                                        ? {
                                              main: palette.grey[50],
                                              contrastText: palette.secondary.main,
                                              hover: palette.grey[100],
                                              light: palette.grey[50],
                                          }
                                        : (palette[props.color as keyof typeof palette] as unknown as {
                                              main: string;
                                              contrastText: string;
                                              hover: string;
                                              light: string;
                                          });
                                if (
                                    !colorPalette ||
                                    !colorPalette.contrastText ||
                                    !colorPalette.main ||
                                    !colorPalette.hover
                                ) {
                                    return undefined;
                                }

                                return {
                                    backgroundColor: colorPalette.main,
                                    color: colorPalette.contrastText,
                                    ':hover': {
                                        backgroundColor: colorPalette.hover,
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: colorPalette.light,
                                    },
                                };
                            },
                        },
                    ],
                }),
                outlined: slotStyle({
                    '&.MuiButton-sizeMedium': {
                        padding: '5px 13px',
                    },
                    '&.MuiButton-sizeSmall': {
                        padding: '3px 9px',
                    },
                    '&.Mui-disabled': {
                        borderColor: palette.grey[100],
                    },
                    variants: [
                        {
                            props: { color: 'inProgress' },
                            style: {
                                backgroundColor: palette.inProgress.light,
                                borderColor: palette.inProgress.main,
                                color: palette.inProgress.contrastText,
                                '&.MuiChip-clickable:hover': {
                                    backgroundColor: palette.inProgress.lightHover,
                                },
                            },
                        },
                        {
                            props: (props: { color: string }) => !!props.color,
                            style: (props: { color: string }) => {
                                const colorPalette =
                                    props.color === 'default'
                                        ? {
                                              main: palette.grey[300],
                                              lightHover: palette.grey[50],
                                              light: undefined,
                                          }
                                        : (palette[props.color as keyof typeof palette] as unknown as {
                                              main: string;
                                              lightHover: string;
                                              light: string;
                                          });

                                if (!colorPalette || !colorPalette.main) {
                                    return undefined;
                                }

                                return {
                                    borderColor: colorPalette.main,
                                    '&.MuiChip-clickable:hover': {
                                        backgroundColor: colorPalette.lightHover,
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: colorPalette.light,
                                        color: colorPalette.light,
                                    },
                                };
                            },
                        },
                    ],
                }),
            },
        },
        MuiAlert: {
            defaultProps: {
                variant: 'standard',
                severity: 'secondary',
                iconMapping: {
                    success: <CheckCircleOutline />,
                    info: <InfoOutlined />,
                    warning: <WarningAmberOutlined />,
                    error: <ErrorOutline />,
                    secondary: <CheckCircleOutline />,
                    primary: <InfoOutlined />,
                    important: <InfoOutlined />,
                },
            },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '14px 16px',
                    '.MuiButtonBase-root': {
                        padding: 0,
                        ':hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                message: {
                    ...typography.body2,
                    color: 'currentcolor',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                },
                icon: {
                    padding: 0,
                },
                action: {
                    paddingTop: 0,
                    marginRight: 0,
                },
                standard: slotStyle({
                    variants: [
                        {
                            props: (props: { severity: string }) => !!props.severity,
                            style: (props: { severity: string }) => {
                                const colorPalette = palette[props.severity as keyof typeof palette] as unknown as {
                                    dark: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.dark || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    color: colorPalette.dark,
                                    backgroundColor: colorPalette.lightHover,
                                };
                            },
                        },
                        {
                            props: { severity: 'important' },
                            style: {
                                alignItems: 'center',
                            },
                        },
                    ],
                }),
                filled: {
                    color: palette.onColor,
                    '.MuiButton-root': {
                        color: palette.onColor,
                    },
                },
            },
        },
        MuiMenu: {
            defaultProps: {
                slotProps: {
                    paper: {
                        elevation: 2,
                    },
                },
            },
            styleOverrides: {
                paper: {
                    boxShadow: 'var(--Paper-shadow)',
                },
            },
        },
        MuiMenuItem: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    ...typography.body1,
                    marginInline: 8,
                    padding: 8,
                    borderRadius: 8,
                    transition: 'background-color .20s ease-in-out',
                    '&:hover': {
                        backgroundColor: palette.cement[50],
                    },
                    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
                        backgroundColor: palette.cement[50],
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: 'transparent',
                    },
                },
                dense: {
                    ...typography.body2,
                    marginInline: 4,
                    paddingInline: 12,
                    paddingBlock: 6,
                },
            },
        },
        MuiPopover: {
            ...(isDark && { styleOverrides: { paper: darkFloating } }),
            defaultProps: {
                slotProps: {
                    paper: {
                        elevation: 2,
                        sx: {
                            boxShadow: 'var(--Paper-shadow)',
                        },
                    },
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.grey[50],
                },
                rectangular: {
                    borderRadius: 12,
                },
            },
            defaultProps: {
                variant: 'rounded',
            },
        },
        MuiLink: {
            styleOverrides: {
                /* Scoped to the class `CardActionLink` sets rather than applied to every link: this is
               the hover for a card's title-row action, and the app's other links keep their own. */
                root: {
                    '&.CardAction-root:hover': {
                        color: palette.purple[500],
                        textDecoration: 'underline',
                    },
                },
                // A link that acts as a button ("Edit", "Skip this run") is a tap target on a phone.
                button: touchTarget,
            },
        },
        MuiIconButton: {
            styleOverrides: {
                // Small icon buttons draw at ~26px; on touch screens their hit area still meets 44px.
                sizeSmall: touchTarget,
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: slotStyle({
                    borderRadius: '12px',
                    boxShadow: 'none',
                    // MUI lightens elevated dark paper; flat surfaces keep the hierarchy readable.
                    ...(isDark && { backgroundImage: 'none' }),
                    variants: [
                        {
                            props: { variant: 'outlined' },
                            style: {
                                border: `1px solid ${palette.grey[100]}`,
                            },
                        },
                        {
                            props: { variant: 'filled' },
                            style: {
                                border: 0,
                                backgroundColor: palette.cement[100],
                            },
                        },
                        {
                            // `filled` in the grey ramp instead of the beige cement one,
                            // for wells sitting on surfaces where cement reads as a tint.
                            props: { variant: 'filledGrey' },
                            style: {
                                border: 0,
                                backgroundColor: palette.grey[50],
                            },
                        },
                        {
                            props: { variant: 'square' },
                            style: {
                                borderRadius: 0,
                            },
                        },
                        {
                            props: { variant: 'squareOutlined' },
                            style: {
                                borderRadius: 0,
                                border: `1px solid ${palette.grey[100]}`,
                            },
                        },
                        {
                            props: { variant: 'squareFilled' },
                            style: {
                                borderRadius: 0,
                                backgroundColor: palette.cement[50],
                                border: `1px solid ${palette.cement[200]}`,
                            },
                        },
                        {
                            props: { variant: 'squareOutlinedFilled' },
                            style: {
                                borderRadius: 0,
                                border: `1px solid ${palette.grey[100]}`,
                                backgroundColor: palette.cement[50],
                            },
                        },
                        {
                            // `filled` one step lighter, in cement/50. No rule: it separates a card from
                            // the page, or a well from its card, by fill alone, so strips nested inside
                            // are not framed twice.
                            props: { variant: 'filledLight' },
                            style: {
                                border: 0,
                                backgroundColor: palette.cement[50],
                            },
                        },
                        {
                            // A strip closing out its card: follows the card's bottom corners, so the
                            // card needs no overflow clip.
                            props: { variant: 'footerTinted' },
                            style: {
                                borderRadius: 0,
                                borderBottomLeftRadius: 'inherit',
                                borderBottomRightRadius: 'inherit',
                                borderTop: `1px solid ${palette.cement[100]}`,
                                backgroundColor: palette.purple[50],
                            },
                        },
                        {
                            props: { variant: 'outlinedFilled' },
                            style: {
                                border: `1px solid ${palette.grey[100]}`,
                                backgroundColor: palette.cement[50],
                            },
                        },
                        {
                            /**
                             * An `outlined` card that toggles, such as a stat tile that doubles as a filter.
                             * Selection is a primary border doubled with an inset ring, as on `answerCard`, so
                             * it reads at a glance without shifting layout.
                             */
                            props: { variant: 'outlinedSelectable' },
                            style: {
                                border: `1px solid ${palette.grey[100]}`,
                                '&:hover': {
                                    borderColor: palette.primary.main,
                                },
                                '&.selected': {
                                    borderColor: palette.primary.main,
                                    boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
                                    backgroundColor: palette.cement[50],
                                },
                            },
                        },
                        {
                            // Larger-radius outlined card used for the 1-800Accountant DQ handoff. See CA-5300.
                            props: { variant: 'partnerCard' },
                            style: {
                                borderRadius: '24px',
                                border: `1px solid ${palette.cement[400]}`,
                            },
                        },
                        {
                            props: { variant: 'emptyStateCard' },
                            style: {
                                padding: '32px',
                                textAlign: 'center',
                                border: `1px dashed ${palette.grey[300]}`,
                                backgroundColor: palette.grey[50],
                            },
                        },
                        {
                            props: { variant: 'loadingCard' },
                            style: {
                                padding: '24px',
                                textAlign: 'center',
                                backgroundColor: palette.grey[50],
                            },
                        },
                        {
                            props: { variant: 'accountSelection' },
                            style: {
                                padding: '16px',
                                border: `1px solid ${palette.grey[200]}`,
                                borderRadius: '8px',
                                marginBottom: '8px',
                                '&:hover': {
                                    borderColor: palette.primary.main,
                                    backgroundColor: palette.action.hover,
                                },
                                '&.selected': {
                                    borderColor: palette.primary.main,
                                },
                            },
                        },
                        {
                            /**
                             * Full-width answer card for focused Q&A flows (the QTE
                             * estimate review). Selection is carried by the border,
                             * doubled with an inset ring so it reads at a 458px width
                             * where 1px of color alone is easy to miss — inset rather
                             * than a thicker border so selecting never shifts layout.
                             */
                            props: { variant: 'answerCard' },
                            style: {
                                padding: '24px',
                                border: `1px solid ${palette.grey[100]}`,
                                borderRadius: '16px',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: palette.primary.main,
                                },
                                '&.selected': {
                                    borderColor: palette.primary.main,
                                    boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
                                },
                            },
                        },
                        {
                            props: { variant: 'pill' },
                            style: {
                                borderRadius: '100px',
                                border: `1px solid ${palette.grey[100]}`,
                                backgroundColor: palette.cement[50],
                                padding: '8px 24px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            },
                        },
                        {
                            /**
                             * White card floating on a white page (the QTE estimate
                             * review's side panel): a soft drop shadow does the
                             * separating, with a hairline in the palette's faintest
                             * grey so the edge still reads where the shadow fades.
                             * Shadow from Figma node 10604-12155.
                             */
                            props: { variant: 'elevatedCard' },
                            style: {
                                borderRadius: '16px',
                                border: `1px solid ${palette.grey[50]}`,
                                backgroundColor: palette.background.paper,
                                boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 8px 24px 0px rgba(0, 0, 0, 0.1)',
                            },
                        },
                        {
                            // Figma "Elevation/card": a white card with a hairline edge and a shallow shadow.
                            props: { variant: 'statusCard' },
                            style: {
                                borderRadius: '16px',
                                border: `1px solid ${alpha(ink, 0.05)}`,
                                backgroundColor: palette.background.paper,
                                boxShadow: DS_ELEVATION_CARD,
                            },
                        },
                        {
                            // Honours MUI's `square` prop on every variant, which each variant's own
                            // radius otherwise overrides. Last, so it wins over those radii. Skips
                            // `elevation`: MUI's Drawer and AppBar pass `square` to their Paper.
                            props: ({ square, variant }: { square?: boolean; variant?: string }) =>
                                !!square && variant !== 'elevation',
                            style: { borderRadius: 0 },
                        },
                    ],
                }),
            },
            defaultProps: {
                elevation: 0,
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    border: `1px solid ${palette.grey[100]}`,
                    padding: '0px',
                    '& .MuiCardHeader-root': {
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingTop: '20px',
                    },
                    '& .MuiCardContent-root': {
                        padding: '24px',
                        ...typography.body1,
                    },
                    '& .MuiCardContent-root:last-child': {
                        paddingBottom: '20px',
                    },
                    '& .MuiCardActions-root': {
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingBottom: '20px',
                        paddingTop: '0px',
                    },
                    '& .MuiCardHeader-title': {
                        ...typography.h3,
                        color: palette.ink,
                    },
                    '& .MuiCardHeader-subheader': {
                        ...typography.body1,
                        marginTop: '8px',
                        color: palette.ink,
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'raisedOnHover' },
                    style: {
                        '&:hover': {
                            boxShadow: '0px 10px 50px -24px rgba(0, 0, 0, 0.15)',
                        },
                    },
                },
                {
                    props: { variant: 'accountCard' },
                    style: {
                        padding: '24px',
                        border: `1px solid ${palette.divider}`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:hover': {
                            backgroundColor: palette.action.hover,
                        },
                    },
                },
            ],
            defaultProps: {
                elevation: 0,
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    border: 'none',
                    '& .MuiDataGrid-columnHeaderTitle': {
                        ...typography.overline,
                        lineHeight: 'normal',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        '&:focus-within': {
                            outline: 'none',
                        },
                        '&:focus': {
                            outline: 'none',
                        },
                        ...typography.overline,
                        borderBottom: 0,
                        textTransform: 'uppercase',
                        color: palette.cement[700],
                        backgroundColor: palette.cement[50],
                        ':first-of-type': {
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                        },
                        ':last-of-type': {
                            borderTopRightRadius: '12px',
                            borderBottomRightRadius: '12px',
                        },
                    },
                    '& .MuiDataGrid-cell': {
                        ...typography.body1,
                        alignContent: 'center',
                        '&:focus-within': {
                            outline: 'none',
                        },
                        '&:focus': {
                            outline: 'none',
                        },
                    },
                    '& .MuiDataGrid-withBorderColor': {
                        borderColor: palette.divider,
                    },
                    '& .MuiDataGrid-row': {
                        transition: 'background-color 0.25s ease-in-out',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: palette.cement[50],
                    },
                    '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
                        backgroundColor: palette.cement[100],
                    },
                },
            },
            defaultProps: {
                pageSizeOptions: [10, 25, 50],
                disableRowSelectionOnClick: true,
                disableColumnFilter: true,
                disableColumnMenu: true,
                disableColumnSelector: true,
                disableDensitySelector: true,
                disableVirtualization: true,
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    ...typography.button,
                },
                root: {
                    '& > svg': {
                        margin: 8,
                    },
                },
                dot: {
                    '&:not(.MuiBadge-colorPrimary, .MuiBadge-colorSecondary, .MuiBadge-colorError, .MuiBadge-colorSuccess, .MuiBadge-colorWarning, .MuiBadge-colorInfo):before':
                        {
                            color: palette.error.main,
                            content: '"--"',
                        },
                },
                colorPrimary: {
                    backgroundColor: palette.primary.main,
                    color: palette.primary.contrastText,
                },
                colorSecondary: {
                    backgroundColor: palette.error.main,
                    color: palette.error.contrastText,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    height: undefined,
                    '&.MuiSwitch-sizeSmall': {
                        width: 50,
                        height: 36,
                        '& .Mui-checked': {
                            transform: 'translateX(14px)',
                        },
                        '& .MuiSwitch-thumb': {
                            margin: 4,
                            width: 20,
                            height: 20,
                            color: palette.common?.white,
                        },
                        '& .MuiSwitch-track': {
                            width: 36,
                            height: 22,
                            borderRadius: '999px',
                        },
                    },
                    '&.MuiSwitch-sizeMedium': {
                        width: 68,
                        height: 52,
                        '& .Mui-checked': {
                            transform: 'translateX(16px)',
                        },
                        '& .MuiSwitch-thumb': {
                            margin: 5,
                            width: 24,
                            height: 24,
                            color: palette.common?.white,
                        },
                        '& .MuiSwitch-track': {
                            width: 44,
                            height: 28,
                            borderRadius: '999px',
                        },
                    },
                    '&.MuiSwitch-sizeLarge': {
                        width: 76,
                        height: 56,
                        '& .Mui-checked': {
                            transform: 'translateX(20px)',
                        },
                        '& .MuiSwitch-thumb': {
                            margin: 5,
                            width: 28,
                            height: 28,
                            color: palette.common?.white,
                        },
                        '& .MuiSwitch-track': {
                            width: 52,
                            height: 32,
                            borderRadius: '999px',
                        },
                    },
                    '& .Mui-disabled': {
                        '& .MuiSwitch-thumb': {
                            color: palette.grey?.[50],
                        },
                    },
                },
                track: {
                    opacity: 1,
                    backgroundColor: palette.grey?.[300],
                    transition: 'all 200ms ease',
                },
                switchBase: {
                    '&:hover': {
                        opacity: 1,
                        '& + .MuiSwitch-track': {
                            backgroundColor: palette.grey?.[200],
                        },
                    },
                    '&.Mui-disabled': {
                        '& + .MuiSwitch-track': {
                            opacity: 1,
                            backgroundColor: palette.grey?.[100],
                        },
                        '& + .MuiSwitch-thumb': {
                            opacity: 1,
                        },
                    },
                    '&.Mui-disabled.Mui-checked': {
                        '& + .MuiSwitch-track': {
                            opacity: 1,
                        },
                        '& + .MuiSwitch-thumb': {
                            opacity: 1,
                        },
                    },
                    variants: [
                        {
                            props: { variant: 'default' },
                            style: {
                                '&.Mui-checked': {
                                    '&:hover': {
                                        opacity: 1,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: palette.green?.[400],
                                        },
                                    },
                                    '& + .MuiSwitch-track': {
                                        opacity: 1,
                                        backgroundColor: palette.green?.[500],
                                    },
                                },
                            },
                        },
                        {
                            props: { variant: 'primary' },
                            style: {
                                '&.Mui-checked': {
                                    '&:hover': {
                                        opacity: 1,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: palette.purple?.[400],
                                        },
                                    },
                                    '& + .MuiSwitch-track': {
                                        opacity: 1,
                                        backgroundColor: palette.primary?.dark,
                                    },
                                },
                            },
                        },
                        {
                            props: { variant: 'secondary' },
                            style: {
                                '&.Mui-checked': {
                                    '&:hover': {
                                        opacity: 1,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: palette.grey?.[700],
                                        },
                                    },
                                    '& + .MuiSwitch-track': {
                                        opacity: 1,
                                        backgroundColor: ink,
                                    },
                                },
                            },
                        },
                    ],
                },
            },
            defaultProps: {
                disableRipple: true,
                variant: 'default',
                size: 'medium',
                style: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    borderRadius: CHECKBOX_RADIUS,
                    '&.Mui-disabled': {
                        '& svg': {
                            color: palette.grey?.[200],
                        },
                    },
                    '&.Mui-focusVisible svg': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                        transition: 'none',
                    },
                },
                colorPrimary: {
                    '& svg': {
                        color: palette.primary?.main,
                        transition: 'all 200ms ease',
                    },
                    ':hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[200]}`,
                    },
                    '&.Mui-checked:hover svg': {
                        color: palette.purple?.[400],
                        boxShadow: 'none',
                    },
                    ':active svg, &:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[100]}`,
                        transition: 'none',
                    },
                    '&.Mui-checked:active svg, &.Mui-checked:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[100]}`,
                        transition: 'none',
                    },
                },
                colorSecondary: {
                    '& svg': {
                        color: palette.secondary?.main,
                        transition: 'all 200ms ease',
                    },
                    ':hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[300]}`,
                    },
                    '&.Mui-checked:hover svg': {
                        color: palette.grey?.[700],
                        boxShadow: 'none',
                    },
                    ':active svg, &:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[200]}`,
                        transition: 'none',
                    },
                    '&.Mui-checked:active svg, &.Mui-checked:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[200]}`,
                        transition: 'none',
                    },
                },
            },
            defaultProps: {
                disableRipple: true,
                size: 'medium',
                checkedIcon: <CheckboxChecked />,
                icon: <CheckboxUnchecked />,
                indeterminateIcon: <CheckboxIndeterminate />,
            },
        },
        MuiRating: {
            styleOverrides: {
                root: {
                    fontSize: '2.5rem',
                    gap: 8,
                    '&.Mui-focusVisible .MuiRating-iconActive': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                        borderRadius: 6,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    '&.Mui-disabled': {
                        '& svg': {
                            color: palette.grey?.[200],
                        },
                    },
                    '&.Mui-focusVisible svg': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                        transition: 'none',
                    },
                    '& svg': {
                        fontSize: '1.25rem',
                    },
                },
                colorPrimary: {
                    '& svg': {
                        color: palette.primary?.main,
                        transition: 'all 200ms ease',
                    },
                    ':hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[200]}`,
                    },
                    '&.Mui-checked:hover svg': {
                        color: palette.purple?.[400],
                        boxShadow: 'none',
                    },
                    ':active svg, &:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[100]}`,
                        transition: 'none',
                    },
                    '&.Mui-checked:active svg, &.Mui-checked:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.purple?.[100]}`,
                        transition: 'none',
                    },
                },
                colorSecondary: {
                    '& svg': {
                        color: palette.secondary?.main,
                        transition: 'all 200ms ease',
                    },
                    ':hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[300]}`,
                    },
                    '&.Mui-checked:hover svg': {
                        color: palette.grey?.[700],
                        boxShadow: 'none',
                    },
                    ':active svg, &:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[200]}`,
                        transition: 'none',
                    },
                    '&.Mui-checked:active svg, &.Mui-checked:active:hover svg': {
                        boxShadow: `0px 0px 0px 2px ${palette.grey?.[200]}`,
                        transition: 'none',
                    },
                },
                sizeSmall: {
                    '& svg': {
                        fontSize: '1rem',
                    },
                },
            },
            defaultProps: {
                disableRipple: true,
                disableTouchRipple: true,
                disableFocusRipple: true,
                size: 'small',
                color: 'secondary',
                checkedIcon: <RadioChecked />,
                icon: <RadioUnchecked />,
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    padding: '0px',
                    '&.Mui-focused': {
                        padding: '0px',
                        borderWidth: '2px',
                        '& > .MuiSelect-select': {
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            paddingLeft: '15px',
                        },
                    },
                    borderRadius: '6px',
                    ...typography.body1,
                    '& > .MuiSvgIcon-root': {
                        color: palette.grey[500],
                        fontSize: '1rem',
                        verticalAlign: 'middle',
                        top: 'initial',
                        right: '16px',
                    },
                    '&.Mui-error.Mui-focused': {
                        padding: '0px',
                    },
                    '& > .MuiSelect-select': {
                        paddingLeft: '16px',
                        paddingTop: '11px',
                        paddingBottom: '11px',
                        '&.MuiInput-input': {
                            paddingRight: '40px',
                        },
                    },
                    variants: [
                        {
                            props: { variant: 'outlined' },
                            style: {
                                '&.MuiSelect-root, &.MuiSelect-root.MuiInputBase-root': {
                                    paddingTop: '0px',
                                    paddingBottom: '0px',
                                    '& .MuiSelect-outlined': {
                                        paddingTop: '24px',
                                        paddingBottom: '7px',
                                        paddingRight: '40px',
                                    },
                                    '&.Mui-focused  .MuiSelect-outlined': {
                                        paddingTop: '23px',
                                        paddingBottom: '6px',
                                        paddingRight: '39px',
                                    },
                                    '& .MuiInputBase-inputSizeSmall': {
                                        paddingTop: '17px',
                                        paddingBottom: '4px',
                                    },
                                },
                                ' .MuiInputLabel-shrink ~ &': {
                                    '&.MuiSelect-root.MuiInputBase-root.Mui-focused': {
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                    },
                                },
                            },
                        },
                    ],
                },
            },
            defaultProps: {
                IconComponent: ChevronDownIcon,
                color: 'secondary',
            },
        },
        MuiPagination: {
            styleOverrides: {
                root: {
                    ...typography.body1,
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    ...typography.subtitle2,
                    borderRadius: 8,
                    '&.Mui-selected': {
                        backgroundColor: palette.cement[100],
                    },
                    '&.MuiPaginationItem-page:hover,&.MuiPaginationItem-previousNext:hover,&.MuiPaginationItem-firstLast:hover':
                        {
                            backgroundColor: palette.cement[300],
                        },
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    marginLeft: '3px',
                    marginRight: '3px',
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    '.MuiTypography-root': {
                        ...typography.body2,
                        color: grey[500],
                    },
                },
            },
            defaultProps: {
                separator: (
                    <ChevronRightIcon
                        sx={{
                            width: 14,
                            height: 14,
                            color: 'grey.500',
                            marginTop: '2px',
                        }}
                    />
                ),
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                endAdornment: {
                    display: 'flex',
                    marginRight: 16,
                    gap: 8,
                },
                root: {
                    '.MuiInput-root': {
                        paddingBlock: '7px',
                        minHeight: 47,
                        flexWrap: 'nowrap',
                        '.MuiInputBase-input': {
                            padding: '0px',
                        },
                        '&.Mui-focused': {
                            paddingBlock: '6px',
                            '.MuiAutocomplete-endAdornment': {
                                marginRight: 15,
                            },
                        },
                    },
                    '.MuiAutocomplete-tag + .MuiInputBase-input.MuiInputBase-inputAdornedStart': {
                        marginLeft: 2,
                    },
                    '.MuiOutlinedInput-root': {
                        paddingLeft: '15px',
                        paddingTop: '15px',
                        paddingBottom: '15px',
                        '.MuiInputBase-input': {
                            padding: '0px',
                        },
                        '&.Mui-focused': {
                            '.MuiAutocomplete-endAdornment': {
                                marginRight: 15,
                            },
                        },
                    },
                },
                option: {
                    marginBottom: 4,
                    '&.Mui-focused, &.Mui-focused:hover': {
                        backgroundColor: cement[50],
                        borderRadius: '6px',
                    },
                    '&.Mui-focused': {
                        backgroundColor: cement[50],
                        color: ink,
                        borderRadius: '6px',
                    },
                },
                listbox: {
                    padding: '6px 12px',
                    color: ink,
                    '&.MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: cement[50],
                        borderRadius: '6px',
                    },
                    '.MuiAutocomplete-option.Mui-focused': {
                        backgroundColor: cement[50],
                    },
                    '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                        backgroundColor: cement[50],
                    },
                    'MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: cement[50],
                    },
                },
                paper: {
                    boxShadow: 'var(--Paper-shadow)',
                    ...(isDark && darkFloating),
                },
                popper: {
                    borderRadius: 6,
                },
                tag: {
                    padding: '0 10px',
                    height: 25,
                    backgroundColor: grey[50],
                    borderColor: grey[50],
                    borderRadius: 12,
                    '&:first-child': {
                        marginLeft: 0,
                    },
                    '& .MuiChip-deleteIcon': {
                        color: grey[500],
                    },
                },
            },
            defaultProps: {
                popupIcon: <ChevronDownIcon style={{ color: ink, width: 16, height: 16 }} />,
                clearIcon: <CancelRounded style={{ color: grey[500], width: 16, height: 16 }} />,
                renderInput: (params) => <TextField {...params} variant="standard" />,
                slotProps: {
                    paper: {
                        elevation: 2,
                    },
                },
            },
        },
        MuiSvgIcon: {
            variants: [
                {
                    props: { fontSize: 'small' },
                    style: {
                        fontSize: '1rem', // 16px
                    },
                },
                {
                    props: { fontSize: 'medium' },
                    style: {
                        fontSize: '1.5rem', // 24px
                    },
                },
                {
                    props: { fontSize: 'large' },
                    style: {
                        fontSize: '2rem', // 32px
                    },
                },
            ],
            defaultProps: {
                fontSize: 'medium',
            },
        },
        MuiMobileStepper: {
            styleOverrides: {
                root: {
                    ...typography.subtitle2,
                    '& .MuiMobileStepper-dots': {
                        gap: '12px',
                    },
                    '& .MuiMobileStepper-dot': {
                        backgroundColor: palette.cement[300],
                    },
                    '& .MuiMobileStepper-dotActive': {
                        backgroundColor: palette.purple[500],
                    },
                    '& .MuiLinearProgress-root': {
                        backgroundColor: palette.grey[200],
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: palette.ink,
                        },
                    },
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    '& .MuiStepLabel-labelContainer': {
                        ...typography.caption,
                        color: palette.text.secondary,
                        '& .MuiStepLabel-label': {
                            ...typography.subtitle1,
                        },
                    },
                    '&.MuiStep-horizontal': {
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        '& .MuiStepLabel-root': {
                            gap: '12px',
                            '& .MuiStepLabel-iconContainer': {
                                paddingRight: '0px',
                            },
                        },
                    },
                    '&.MuiStepper-vertical': {
                        '& .MuiStepLabel-root': {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                        },
                        paddingTop: '3px',
                        paddingBottom: '3px',
                    },
                    '& .MuiStepContent-root': {
                        borderColor: palette.grey[100],
                    },

                    variants: [
                        {
                            props: { active: true, completed: false },
                            style: {
                                '& .MuiStepLabel-labelContainer': {
                                    color: palette.text.secondary,
                                    '& .MuiStepLabel-label': {
                                        color: palette.text.primary,
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    '&.MuiStepConnector-horizontal': {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                    },
                    '& .MuiStepConnector-line': {
                        borderColor: palette.grey[100],
                    },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: slotStyle({
                    borderRadius: 100,
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 100,
                    },
                    variants: [
                        {
                            style: ({ color }: { color: string }) => {
                                const colorPalette = palette[color as keyof typeof palette] as unknown as {
                                    main: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.main || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    backgroundColor: colorPalette.lightHover,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: colorPalette.main,
                                    },
                                };
                            },
                        },
                    ],
                }),
            },
            defaultProps: {
                color: 'secondary',
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    strokeLinecap: 'round',
                },
            },
            defaultProps: {
                color: 'secondary',
            },
        },
        MuiToggleButton: {
            defaultProps: {
                color: 'info',
                disableRipple: true,
            },
            styleOverrides: {
                root: slotStyle({
                    ...typography.body1,
                    textAlign: 'left',
                    borderRadius: 8,
                    padding: 12,
                    border: 'none',
                    '&.Mui-disabled': {
                        borderColor: palette.grey[100],
                    },
                    '&.Mui-focusVisible': {
                        outline: `3px solid ${outlineBlue}`,
                        outlineOffset: 3,
                    },
                    variants: [
                        {
                            style: ({ color }: { color: string }) => {
                                const colorPalette = palette[color as keyof typeof palette] as unknown as {
                                    light: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.light || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    color: palette.ink,
                                    '&.Mui-selected': {
                                        backgroundColor: colorPalette.light,
                                        color: palette.ink,

                                        '&:hover': {
                                            backgroundColor: colorPalette.lightHover,
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: colorPalette.lightHover,
                                    },
                                };
                            },
                        },
                    ],
                }),
            },
        },
        MuiToggleButtonGroup: {
            defaultProps: {
                color: 'info',
            },
            styleOverrides: {
                root: {
                    columnGap: 16,
                    rowGap: 12,
                    variants: [
                        {
                            props: { variant: 'outlined' },
                            style: {
                                border: '1px solid',
                                borderColor: palette.grey[100],
                                padding: 4,
                                borderRadius: 12,
                            },
                        },
                        {
                            props: { orientation: 'horizontal' },
                            style: {
                                '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                                    marginLeft: 0,
                                },
                            },
                        },
                        {
                            props: { orientation: 'vertical' },
                            style: {
                                '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                                    marginTop: 0,
                                },
                            },
                        },
                        {
                            props: { orientation: 'horizontal', variant: 'outlinedControl' },
                            style: {
                                '& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected':
                                    {
                                        borderLeftWidth: '2px',
                                        borderLeftStyle: 'solid',
                                        borderLeftColor: palette.ink,
                                    },
                            },
                        },
                        {
                            props: { orientation: 'vertical', variant: 'outlinedControl' },
                            style: {
                                '& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected':
                                    {
                                        borderTopWidth: '2px',
                                        borderTopStyle: 'solid',
                                        borderTopColor: palette.ink,
                                    },
                            },
                        },
                    ],
                },
                grouped: slotStyle({
                    borderRadius: 8,
                    border: 'none',
                    transition: 'background-color .20s ease',
                    variants: [
                        {
                            props: { variant: 'outlinedControl' },
                            style: ({ color }: { color: string }) => {
                                const colorPalette = palette[color as keyof typeof palette] as unknown as {
                                    light: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.light || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    minWidth: 160,
                                    color: palette.ink,
                                    backgroundColor: palette.background.paper,
                                    border: '1px solid',
                                    padding: '23px',
                                    borderRadius: 12,
                                    borderColor: colorPalette.light,
                                    '&.Mui-selected': {
                                        backgroundColor: palette.background.paper,
                                        border: '2px solid',
                                        borderColor: palette.ink,
                                        padding: '22px',
                                        '&:hover': {
                                            backgroundColor: palette.cement[50],
                                        },
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: palette.grey[100],
                                        color: palette.grey[100],
                                    },
                                    '&:hover': {
                                        borderColor: palette.ink,
                                        backgroundColor: palette.cement[50],
                                    },
                                    '&.Mui-error': {
                                        borderColor: palette.error.main,
                                        '&:hover': {
                                            borderColor: palette.error.main,
                                        },
                                    },
                                };
                            },
                        },
                        {
                            props: { variant: 'filledControl' },
                            style: ({ color }: { color: string }) => {
                                const colorPalette = palette[color as keyof typeof palette] as unknown as {
                                    light: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.light || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    minWidth: 160,
                                    color: palette.ink,
                                    backgroundColor: colorPalette.lightHover,
                                    padding: '24px',
                                    borderRadius: 12,
                                    '&.Mui-selected': {
                                        border: '2px solid',
                                        padding: '22px',
                                        backgroundColor: colorPalette.lightHover,
                                        '&:hover': {
                                            backgroundColor: palette.background.paper,
                                            border: '2px solid',
                                            padding: '22px',
                                        },
                                    },
                                    '&:hover': {
                                        border: '1px solid',
                                        borderColor: colorPalette.light,
                                        padding: '23px',
                                    },
                                };
                            },
                        },
                        {
                            props: ({ variant }: { variant?: string }) =>
                                variant !== 'outlinedControl' && variant !== 'filledControl',
                            style: ({ color }: { color: string }) => {
                                const colorPalette = palette[color as keyof typeof palette] as unknown as {
                                    light: string;
                                    lightHover: string;
                                };
                                if (!colorPalette || !colorPalette.light || !colorPalette.lightHover) {
                                    return undefined;
                                }

                                return {
                                    color: palette.ink,
                                    '&.Mui-selected': {
                                        backgroundColor: colorPalette.light,
                                        color: palette.ink,

                                        '&:hover': {
                                            backgroundColor: colorPalette.lightHover,
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: colorPalette.lightHover,
                                    },
                                };
                            },
                        },
                    ],
                }),
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    borderRadius: '100px',
                    '& .MuiSlider-thumb': {
                        border: '1px solid white',
                    },
                    '& .MuiSlider-valueLabelOpen': {
                        ...typography.subtitle2,
                        borderRadius: 4,
                        backgroundColor: palette.grey[800],
                    },
                    '& .MuiSlider-mark': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiSlider-markLabel': {
                        ...typography.body2,
                        color: palette.text.secondary,
                    },
                },
            },
            defaultProps: {
                valueLabelDisplay: 'auto',
            },
        },
        MuiListItemAvatar: {
            styleOverrides: {
                root: {
                    minWidth: 50,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    ...typography.subtitle1,
                    marginBlock: 4,
                    color: palette.text.primary,
                    '&:not(:has(.MuiListItemButton-root))': {
                        padding: 10,
                        borderRadius: 8,
                        '&.MuiListItem-divider': {
                            borderRadius: 0,
                        },
                    },
                    // A row carrying a count chip is a label and a number competing for one narrow
                    // line -- the hub's filter sidebars, where a queue section name meets its task
                    // count. The two boxes are flush by default, and a label with no break point
                    // inside it (one long word, or a stage name off the API) overruns its own box and
                    // reads underneath the chip. Scoped to rows that have a chip so ordinary rows
                    // keep the icon spacing they were laid out with.
                    '&:has(> .MuiChip-root)': {
                        gap: 8,
                        '& .MuiListItemText-primary': {
                            overflowWrap: 'anywhere',
                        },
                    },
                },
                dense: {
                    '&:not(:has(.MuiListItemButton-root))': {
                        paddingTop: 6,
                        paddingBottom: 6,
                    },
                    '& .MuiListItemText-primary': {
                        ...typography.subtitle2,
                    },
                    '& .MuiListItemText-secondary': {
                        ...typography.body2,
                        disablePadding: true,
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    padding: 10,
                    borderRadius: 8,
                    '&:hover': {
                        backgroundColor: palette.cement[50],
                    },
                    '&.Mui-selected': {
                        backgroundColor: palette.cement[100],
                        '&:hover': {
                            backgroundColor: palette.cement[100],
                        },
                        '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiListItemSecondaryAction-root, & svg':
                            {
                                color: palette.text.primary,
                            },
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        '& .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiListItemIcon-root, & .MuiListItemSecondaryAction-root, & svg':
                            {
                                color: palette.text.disabled,
                            },
                    },
                },
                dense: {
                    paddingTop: 6,
                    paddingBottom: 6,
                    '& .MuiListItemText-secondary': {
                        ...typography.body2,
                    },
                },
            },
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    margin: 0,
                },
                primary: {
                    ...typography.subtitle1,
                    color: palette.cement[700],
                },
                secondary: {
                    ...typography.body2,
                    color: palette.text.secondary,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 16,
                    color: palette.cement[700],
                    paddingLeft: 4,
                    paddingRight: 10,
                    '& .MuiSvgIcon-root': {
                        fontSize: '1em',
                    },
                },
            },
        },
        MuiListItemSecondaryAction: {
            styleOverrides: {
                root: {
                    '& .MuiButtonBase-root': {
                        fontSize: '1em',
                        minWidth: 16,
                        color: palette.cement[700],
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    paddingTop: '16px',
                    paddingBottom: '16px',
                    '&.Mui-disabled': {
                        backgroundColor: 'initial',
                    },
                    '&:not(:first-of-type)': {
                        borderTop: `1px solid ${palette.divider}`,
                    },
                    '&:before': {
                        display: 'none',
                    },
                    '&.Mui-expanded': {
                        margin: 'auto',
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    ...typography.body1,
                    color: palette.secondary.light,
                    paddingTop: '16px',
                    paddingRight: '16px',
                    paddingLeft: '0px',
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    ...typography.body1,
                    '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '0px',
                        paddingRight: '16px',
                    },
                    '&.Mui-expanded': {
                        minHeight: 'auto', // default
                    },
                    minHeight: 'auto',
                    padding: '0px',
                },
            },
            defaultProps: {
                expandIcon: <ChevronDownIcon fontSize="small" />,
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: slotStyle({
                    variants: [
                        // TODO:: Current implementation is wrong, do not change, should be using maxWidth.
                        {
                            props: { fullScreen: true },
                            style: (props: { theme: Theme }) => ({
                                borderRadius: 0,
                                [props.theme.breakpoints.down('sm')]: {
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        padding: '26px 24px',
                                    },
                                },
                                [props.theme.breakpoints.up('sm')]: {
                                    margin: 'auto',
                                    width: '100%',
                                    maxWidth: '100%',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        padding: '24px',
                                    },
                                },
                            }),
                        },
                        {
                            props: { size: 'xsmall', fullScreen: false },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '360px',
                                    maxWidth: '100%',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '24px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    width: '100%',
                                    maxWidth: '100%',
                                    height: 'calc(100% - 209px)',
                                    alignSelf: 'flex-end',
                                    margin: 0,
                                    borderBottomRightRadius: '0px',
                                    borderBottomLeftRadius: '0px',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '26px 24px',
                                    },
                                },
                            }),
                        },
                        {
                            props: { size: 'small', fullScreen: false },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '480px',
                                    maxWidth: '100%',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '24px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    height: 'calc(100% - 209px)',
                                    width: '100%',
                                    maxWidth: '100%',
                                    alignSelf: 'flex-end',
                                    margin: 0,
                                    borderBottomRightRadius: '0px',
                                    borderBottomLeftRadius: '0px',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '26px 24px',
                                    },
                                },
                            }),
                        },
                        {
                            props: { size: 'medium', fullScreen: false },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '600px',
                                    maxWidth: '100%',
                                    minHeight: 300,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '24px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    height: 'calc(100% - 209px)',
                                    width: '100%',
                                    maxWidth: '100%',
                                    alignSelf: 'flex-end',
                                    margin: 0,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '26px 24px',
                                    },
                                    borderBottomRightRadius: '0px',
                                    borderBottomLeftRadius: '0px',
                                },
                            }),
                        },
                        {
                            props: { size: 'large', fullScreen: false },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '900px',
                                    maxWidth: '100%',
                                    minHeight: 300,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '24px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    height: 'calc(100% - 209px)',
                                    width: '100%',
                                    maxWidth: '100%',
                                    alignSelf: 'flex-end',
                                    margin: 0,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '26px 24px',
                                    },
                                    borderBottomRightRadius: '0px',
                                    borderBottomLeftRadius: '0px',
                                },
                            }),
                        },
                        {
                            props: { size: 'xlarge', fullScreen: false },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '1200px',
                                    maxWidth: '100%',
                                    minHeight: 300,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h3,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '24px',
                                    },
                                    '& .MuiDialogContent-root': {
                                        maxWidth: '748px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    height: 'calc(100% - 209px)',
                                    width: '100%',
                                    maxWidth: '100%',
                                    alignSelf: 'flex-end',
                                    margin: 0,
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h5,
                                        borderBottom: `1px solid ${palette.grey?.[100]}`,
                                        padding: '26px 24px',
                                    },
                                    borderBottomRightRadius: '0px',
                                    borderBottomLeftRadius: '0px',
                                },
                            }),
                        },
                        {
                            props: { variant: 'transactionModal' },
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.up('sm')]: {
                                    width: '90vw',
                                    maxWidth: '1400px',
                                    height: '85vh',
                                    maxHeight: '95vh',
                                    margin: '2.5vh auto',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h6,
                                        padding: '16px 24px',
                                        paddingBottom: '8px',
                                    },
                                    '& .MuiDialogContent-root': {
                                        paddingBottom: '16px',
                                    },
                                },
                                [props.theme.breakpoints.down('sm')]: {
                                    width: '95vw',
                                    height: '95vh',
                                    margin: '2.5vh auto',
                                    '& .MuiDialogTitle-root': {
                                        ...typography.h6,
                                        padding: '16px 24px',
                                        paddingBottom: '8px',
                                    },
                                },
                            }),
                        },
                    ],
                    padding: 0,
                    borderRadius: 20,
                }),
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    paddingTop: '24px!important', // Todo: override might not be needed
                    padding: '24px',
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    ...typography.body2,
                    color: ink,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: slotStyle({
                    borderTop: `1px solid ${palette.grey?.[100]}`,
                    padding: '16px 24px',
                    variants: [
                        {
                            // Phones: buttons share the row and keep their labels on one line; a pair that
                            // can't fit stacks instead of wrapping "Dismiss employee" over two lines. Reverse
                            // wrapping puts the confirming action (last in the row) on top once they stack.
                            props: {},
                            style: (props: { theme: Theme }) => ({
                                [props.theme.breakpoints.down('sm')]: {
                                    flexWrap: 'wrap-reverse',
                                    gap: props.theme.spacing(1),
                                    '& > :not(style) ~ :not(style)': { marginLeft: 0 },
                                    '& > .MuiButton-root': { flex: '1 1 auto', whiteSpace: 'nowrap' },
                                },
                            }),
                        },
                    ],
                }),
            },
        },
        MuiPickersPopper: {
            styleOverrides: {
                paper: {
                    boxShadow: 'var(--Paper-shadow)',
                    marginTop: 4,
                },
            },
        },
        // The arrows take their colour from the button, so a month that can't be reached greys out.
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: {
                    color: palette.grey[700],
                    '&.Mui-disabled': { color: palette.action.disabled },
                },
            },
        },
        MuiPickersCalendarHeader: {
            defaultProps: {
                slots: {
                    rightArrowIcon: ChevronRightIcon,
                    leftArrowIcon: ChevronLeftIcon,
                    switchViewIcon: ChevronDownIcon,
                },
                slotProps: {
                    rightArrowIcon: {
                        sx: {
                            width: 24,
                            height: 24,
                        },
                    },
                    leftArrowIcon: {
                        sx: {
                            width: 24,
                            height: 24,
                        },
                    },
                    switchViewIcon: {
                        sx: {
                            color: palette.grey[900],
                            width: 16,
                            height: 16,
                        },
                    },
                },
            },
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${palette.grey?.[100]}`,
                    margin: '0px',
                    padding: '12px',
                    minHeight: 'auto',
                    maxHeight: '48px',
                },
                label: {
                    ...typography.h5,
                    color: palette.text.primary,
                },
            },
        },
        MuiPickersToolbar: {
            styleOverrides: {
                root: {
                    padding: '16px 12px',
                },
            },
        },
        MuiDatePickerToolbar: {
            styleOverrides: {
                title: {
                    marginTop: 4,
                },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: {
                    ...typography.body2,
                    color: palette.text.secondary,
                },
                weekNumberLabel: {
                    ...typography.body2,
                    color: palette.text.disabled,
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    ...typography.body2,
                    color: palette.text.primary,
                },
            },
        },
        MuiPickersMonth: {
            styleOverrides: {
                root: {
                    ...typography.subtitle2,
                    color: palette.text.primary,
                },
            },
        },
        MuiPickersLayout: {
            defaultProps: {
                slotProps: {
                    actionBar: {
                        sx: {
                            gap: '16px',
                            padding: '8px 20px',
                            border: 'none',
                            '& .MuiButton-root': {
                                backgroundColor: 'transparent',
                                color: palette.text.primary,
                                padding: '11px 0px',
                                minWidth: 0,
                                borderRadius: 0,
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: palette.primary.main,
                                },
                            },
                        },
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${palette.cement[200]}`,
                        '& .MuiTableBody': {
                            ...typography.overline,
                            '&:hover': {
                                backgroundColor: palette.cement[50],
                            },
                        },
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        backgroundColor: palette.background.paper,
                        '&:hover': {
                            backgroundColor: palette.cement[50],
                        },
                    },
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '.MuiTableCell-head': {
                        ...typography.overline,
                        borderBottom: 0,
                        textTransform: 'uppercase',
                        color: palette.cement[700],
                        padding: '10px 16px',
                        backgroundColor: palette.cement[50],
                        ':first-of-type': {
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                        },
                        ':last-of-type': {
                            borderTopRightRadius: '12px',
                            borderBottomRightRadius: '12px',
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                body: {
                    ...typography.body1,
                },
            },
        },
    };
}
